/**
 * Módulo Clientes — `/clientes`.
 *
 * Esta página **orquesta**: resuelve el rol, trae el portfolio, deriva los
 * totales y elige la vista. Cada bloque es un componente de esta carpeta, así que
 * la página se lee de arriba a abajo como el orden de la pantalla.
 *
 * Tres vistas en una ruta: la tabla del portfolio y la brecha de activación se
 * eligen con el selector; la ficha de una cuenta abre en drawer sobre cualquiera
 * de las dos.
 *
 * La ficha abierta **es la URL** (`/clientes/{id}`), no un `useState`. En un
 * panel cuya tesis es que todo linkea a su registro exacto, los registros del
 * propio panel también tienen dirección: refrescar no la cierra, se puede pegar
 * el link en un chat, y los links que ya generaban /entrevistas y las alertas
 * del Overview llegan a la ficha en vez de a la 404.
 *
 * Qué NO hace acá el front: decidir permisos. El payload de ops y comercial llega
 * con `mrr: null` en cada cuenta porque el servidor lo recorta. El chequeo de rol
 * de esta página solo decide si dibuja la columna, no si el dato existe.
 */
import { CLIENT_STATE_LABELS, formatMetricValue, formatPeriodo } from '@ops/contracts'
import { useMemo, useState } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'

import { useClients, useMe, useMetrics } from '../../lib/queries'
import { PATRON_CLIENTE, RUTA_CLIENTES, rutaCliente } from '../../lib/rutas'
import { usePeriodo } from '../../shell'
import { Card, Section, TriageBar, type TriageConteo } from '../../ui'
import { BrechaActivacion } from './BrechaActivacion'
import { ClienteDrawer } from './ClienteDrawer'
import { ClientesKpis } from './ClientesKpis'
import { PortfolioFiltros } from './PortfolioFiltros'
import { PortfolioTabla } from './PortfolioTabla'
import { SelectorVista, idPanelVista, type Vista } from './SelectorVista'
import { Cargando, ErrorDeCarga, Vacio } from '../comunes'
import {
  FILTROS_VACIOS,
  duenosDe,
  filtrarClientes,
  ordenarPorRiesgo,
  totalesPortfolio,
  verticalesDe,
  type FiltrosPortfolio,
} from './derivados'

export default function ClientesPage() {
  const { periodo } = usePeriodo()
  // Un solo reloj para toda la pantalla: si cada badge leyera el suyo, dos
  // cifras del mismo período podrían decir frescuras distintas.
  const [ahora] = useState(() => new Date())

  const me = useMe()
  const verPlata = me.data?.rol === 'admin'
  const puedeCargar = me.data !== undefined

  const portfolio = useClients(periodo)
  /*
   * Regla 1 en la tabla del portfolio. `/api/clients` no manda `provenance` por
   * fila —`Client` no tiene el campo—, así que la frescura sale del catálogo de
   * métricas, que es la misma carga que alimenta cada grupo de columnas:
   * `clientes_activos` y `brecha_activacion_total` vienen de la plataforma, y
   * el MRR de Stripe. Sin esto la tabla más densa de la app —unos 90 números—
   * era la única sin fuente ni frescura a la vista.
   */
  const metrics = useMetrics(periodo)
  const provenienciaDe = (slug: string) =>
    metrics.data?.valores.find((v) => v.slug === slug)?.provenance ?? null

  const [vista, setVista] = useState<Vista>('portfolio')
  const [filtros, setFiltros] = useState<FiltrosPortfolio>(FILTROS_VACIOS)

  // La cuenta abierta se lee del patrón de la ruta y no de `useParams`: la ficha
  // es una ruta hija, así que sus params no llegan al elemento del padre.
  // `useMatch` los trae ya decodificados y sin acoplar la página al anidamiento.
  const navegar = useNavigate()
  const abierta = useMatch(PATRON_CLIENTE)?.params.clientId ?? null
  const abrir = (clientId: string) => navegar(rutaCliente(clientId))
  // Cerrar es navegar, no borrar estado: el botón atrás también cierra.
  const cerrar = () => navegar(RUTA_CLIENTES)

  const todas = portfolio.data?.clientes ?? []
  const filtradas = useMemo(
    () => ordenarPorRiesgo(filtrarClientes(todas, filtros), ahora),
    [todas, filtros, ahora],
  )
  const totalesPortfolioCompleto = useMemo(() => totalesPortfolio(todas), [todas])
  const totalesVista = useMemo(() => totalesPortfolio(filtradas), [filtradas])
  const verticales = useMemo(() => verticalesDe(todas), [todas])
  const duenos = useMemo(() => duenosDe(todas), [todas])

  if (portfolio.isPending) return <Cargando que="el portfolio" />

  if (portfolio.isError) {
    return (
      <ErrorDeCarga
        que="el portfolio"
        error={portfolio.error}
        onRetry={() => void portfolio.refetch()}
        reintentando={portfolio.isFetching}
      />
    )
  }

  if (todas.length === 0) {
    return (
      <Vacio punteada
        mensaje={`No hay ninguna cuenta cargada para ${formatPeriodo(periodo)}.`}
        detalle="Cambiá de período en el encabezado o cargá las cuentas en Admin."
      />
    )
  }

  const pendientes = todas.filter((c) => c.estado === 'pendiente_implementacion')
  const enRetencion = todas.filter((c) => c.estado === 'retencion')
  const enCero = totalesPortfolioCompleto.enCero

  const conteos: TriageConteo[] = [
    {
      severidad: 'critical',
      cantidad: enCero.length,
      label: 'cuentas sin un solo empleado corriendo',
      onClick: () => setVista('brecha'),
    },
    {
      severidad: pendientes.length > 6 ? 'critical' : 'warning',
      cantidad: pendientes.length,
      label: CLIENT_STATE_LABELS.pendiente_implementacion.toLowerCase(),
      onClick: () => {
        setVista('portfolio')
        setFiltros({ ...FILTROS_VACIOS, estado: 'pendiente_implementacion' })
      },
    },
    {
      severidad: 'warning',
      cantidad: enRetencion.length,
      label: 'cuentas en retención',
      onClick: () => {
        setVista('portfolio')
        setFiltros({ ...FILTROS_VACIOS, estado: 'retencion' })
      },
    },
    {
      severidad: 'unknown',
      cantidad: totalesPortfolioCompleto.sinMedicion,
      label: 'cuentas sin medición de activación',
    },
  ]

  const estadoGeneral =
    enCero.length > 0 ? 'critical' : pendientes.length > 0 ? 'warning' : 'ok'

  return (
    <>
      <TriageBar
        estado={estadoGeneral}
        titular={
          enCero.length > 0
            ? `${enCero.length} ${enCero.length === 1 ? 'cuenta' : 'cuentas'} sin un solo empleado corriendo`
            : 'Todas las cuentas tienen al menos un empleado corriendo'
        }
        conteos={conteos}
        meta={`portfolio de ${formatPeriodo(periodo)} · ${todas.length} cuentas`}
        className="mb-4 rounded-card border border-border"
      />

      <ClientesKpis
        periodo={periodo}
        ahora={ahora}
        totales={totalesPortfolioCompleto}
        verPlata={verPlata}
        puedeCargar={puedeCargar}
        onVerEnCero={() => setVista('brecha')}
      />

      <div className="mb-4">
        <SelectorVista
          vista={vista}
          onChange={setVista}
          opciones={[
            {
              vista: 'portfolio',
              label: 'Tabla del portfolio',
              icono: 'clientes',
              cantidad: todas.length,
              proveniencia: `plataforma · cuentas del portfolio de ${formatPeriodo(periodo)}`,
            },
            {
              vista: 'brecha',
              label: 'Brecha de activación',
              icono: 'plano',
              cantidad: totalesPortfolioCompleto.brecha,
              proveniencia: `derivado · Σ (contratados − instalados) sobre las ${todas.length - totalesPortfolioCompleto.sinMedicion} cuentas con medición de ${formatPeriodo(periodo)}`,
            },
          ]}
        />
      </div>

      {vista === 'portfolio' ? (
        <div
          role="tabpanel"
          id={idPanelVista('portfolio')}
          aria-labelledby="tab-portfolio"
          tabIndex={0}
        >
          <Section
            titulo="Quién está trabado, y desde cuándo"
            bajada={
              <>
                El portfolio de {formatPeriodo(periodo)} son{' '}
                <b>{todas.length} cuentas</b>. <b>{enCero.length}</b> tienen
                empleados contratados y ninguno corriendo, y{' '}
                <b>{pendientes.length}</b> siguen pendientes de implementación. La
                tabla arranca ordenada por riesgo: la fila crítica respira, la sana
                va compacta. Clickeá una fila para abrir su ficha.
              </>
            }
            espaciado="sm"
          >
            <Card padding="none" hundida className="mb-2">
              <PortfolioFiltros
                filtros={filtros}
                onChange={setFiltros}
                verticales={verticales}
                duenos={duenos}
                resumen={
                  <>
                    <b>{filtradas.length}</b> de <b>{todas.length}</b> cuentas
                    {totalesVista.contratados > 0 ? (
                      <>
                        {' · '}
                        <b>
                          {totalesVista.instalados} de {totalesVista.contratados}
                        </b>{' '}
                        empleados instalados
                      </>
                    ) : null}
                  </>
                }
              />
            </Card>

            <PortfolioTabla
              clientes={filtradas}
              links={portfolio.data.links.clientes}
              ahora={ahora}
              verPlata={verPlata}
              onAbrir={abrir}
              caption={`Portfolio de ${formatPeriodo(periodo)}. Estado y dueño los mantiene el equipo en el panel; contratados e instalados salen de la plataforma${verPlata ? '; el MRR, de Stripe' : ''}.`}
              proveniencias={[
                {
                  columnas: 'Instalados / contratados',
                  provenance: provenienciaDe('brecha_activacion_total'),
                },
                ...(verPlata
                  ? [
                      {
                        columnas: 'MRR',
                        provenance: provenienciaDe('mrr'),
                      },
                    ]
                  : []),
              ]}
              footer={
                <span className="tabular">
                  {filtradas.length} cuentas en esta vista ·{' '}
                  {totalesVista.instalados} de {totalesVista.contratados} empleados
                  instalados
                  {totalesVista.pctActivado !== null
                    ? ` (${formatMetricValue(totalesVista.pctActivado, 'percent')})`
                    : ''}
                  {verPlata && totalesVista.mrr !== null
                    ? ` · ${formatMetricValue(totalesVista.mrr, 'usd')} de MRR`
                    : ''}
                </span>
              }
            />
          </Section>
        </div>
      ) : (
        <div
          role="tabpanel"
          id={idPanelVista('brecha')}
          aria-labelledby="tab-brecha"
          tabIndex={0}
        >
          <Section
            titulo="Dinero contratado que no está corriendo"
            bajada={
              <>
                <b>{totalesPortfolioCompleto.brecha} empleados digitales</b> están
                contratados y no instalados, sobre{' '}
                <b>{totalesPortfolioCompleto.contratados}</b> contratados en las
                cuentas con medición. El ranking va de mayor brecha a menor y el
                peor caso está arriba, escrito, para que no se lea como una fila
                más.
              </>
            }
            espaciado="sm"
          >
            <BrechaActivacion
              periodo={periodo}
              clientes={todas}
              verPlata={verPlata}
              onAbrir={abrir}
            />
          </Section>
        </div>
      )}

      {abierta !== null ? (
        <ClienteDrawer
          clientId={abierta}
          periodo={periodo}
          ahora={ahora}
          verPlata={verPlata}
          onClose={cerrar}
        />
      ) : null}
    </>
  )
}
